import React, { useState, useEffect } from 'react';
import { Plus, X, Check, Edit2, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { toast } from 'sonner';

/**
 * CustomMaterialsManager
 * A reusable component for managing custom materials in calculator tabs
 * Allows users to add, edit, and remove materials from the dropdown
 */
const CustomMaterialsManager = ({ 
  materials,  // Current materials list (from parent state or default database)
  onMaterialsChange, // Callback when materials are updated
  storageKey, // localStorage key for persistence
  materialType = 'demo', // 'demo' | 'bulk' | 'excavation'
  weightLabel = 'lbs/sq ft', // Label for weight field
  weightField = 'weightPerSqFt' // Field name for weight value
}) => {
  const [customMaterials, setCustomMaterials] = useState(() => {
    const saved = localStorage.getItem(`${storageKey}_custom`);
    return saved ? JSON.parse(saved) : [];
  });

  const [showAddForm, setShowAddForm] = useState(false);
  const [newMaterial, setNewMaterial] = useState({
    name: '',
    category: 'Custom',
    weight: '',
    description: ''
  });

  const [editingId, setEditingId] = useState(null);
  const [editValues, setEditValues] = useState({});

  // Save custom materials to localStorage
  useEffect(() => {
    localStorage.setItem(`${storageKey}_custom`, JSON.stringify(customMaterials));
    // Notify parent of combined materials list
    if (onMaterialsChange) {
      onMaterialsChange([...materials, ...customMaterials]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customMaterials, storageKey]);

  const addCustomMaterial = () => {
    if (!newMaterial.name.trim()) {
      toast.error('Please enter a material name');
      return;
    }
    if (!newMaterial.weight || parseFloat(newMaterial.weight) <= 0) {
      toast.error('Please enter a valid weight');
      return;
    }

    const material = {
      id: `custom_${Date.now()}`,
      name: newMaterial.name,
      category: newMaterial.category || 'Custom',
      [weightField]: parseFloat(newMaterial.weight),
      description: newMaterial.description || `Custom material`,
      isCustom: true
    };

    setCustomMaterials([...customMaterials, material]);
    setNewMaterial({ name: '', category: 'Custom', weight: '', description: '' });
    setShowAddForm(false);
    toast.success(`Material "${material.name}" added`);
  };

  const updateCustomMaterial = (id) => {
    setCustomMaterials(customMaterials.map(m => {
      if (m.id === id) {
        return {
          ...m,
          name: editValues.name || m.name,
          category: editValues.category || m.category,
          [weightField]: parseFloat(editValues.weight) || m[weightField],
          description: editValues.description || m.description
        };
      }
      return m;
    }));
    setEditingId(null);
    setEditValues({});
    toast.success('Material updated');
  };

  const removeCustomMaterial = (id) => {
    setCustomMaterials(customMaterials.filter(m => m.id !== id));
    toast.success('Material removed');
  };

  const startEditing = (material) => {
    setEditingId(material.id);
    setEditValues({
      name: material.name,
      category: material.category,
      weight: material[weightField],
      description: material.description
    });
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="ml-2">
          <Settings className="w-4 h-4 mr-1" />
          Manage
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage Custom Materials</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Add New Material Form */}
          {showAddForm ? (
            <Card className="bg-muted/30">
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Material Name</Label>
                    <Input
                      value={newMaterial.name}
                      onChange={(e) => setNewMaterial({...newMaterial, name: e.target.value})}
                      placeholder="e.g., Custom Concrete"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Category</Label>
                    <Input
                      value={newMaterial.category}
                      onChange={(e) => setNewMaterial({...newMaterial, category: e.target.value})}
                      placeholder="Custom"
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{weightLabel}</Label>
                    <Input
                      type="number"
                      value={newMaterial.weight}
                      onChange={(e) => setNewMaterial({...newMaterial, weight: e.target.value})}
                      placeholder="0.00"
                      className="h-8 text-sm"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Description (optional)</Label>
                    <Input
                      value={newMaterial.description}
                      onChange={(e) => setNewMaterial({...newMaterial, description: e.target.value})}
                      placeholder="Brief description"
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={addCustomMaterial}>
                    <Check className="w-3 h-3 mr-1" />
                    Add Material
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowAddForm(false)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setShowAddForm(true)} className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Add New Custom Material
            </Button>
          )}

          {/* Custom Materials List */}
          {customMaterials.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">Your Custom Materials</Label>
              {customMaterials.map(material => (
                <div key={material.id} className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                  {editingId === material.id ? (
                    <>
                      <Input
                        value={editValues.name}
                        onChange={(e) => setEditValues({...editValues, name: e.target.value})}
                        className="h-7 text-sm flex-1"
                      />
                      <Input
                        type="number"
                        value={editValues.weight}
                        onChange={(e) => setEditValues({...editValues, weight: e.target.value})}
                        className="h-7 text-sm w-20"
                      />
                      <Button size="sm" variant="ghost" onClick={() => updateCustomMaterial(material.id)} className="h-7 px-2">
                        <Check className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-7 px-2">
                        <X className="w-3 h-3" />
                      </Button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm">{material.name}</span>
                      <span className="text-xs text-muted-foreground">{material.category}</span>
                      <span className="text-xs font-medium text-primary">{material[weightField]} {weightLabel}</span>
                      <Button size="sm" variant="ghost" onClick={() => startEditing(material)} className="h-7 px-2">
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => removeCustomMaterial(material.id)} className="h-7 px-2">
                        <X className="w-3 h-3 text-destructive" />
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Default Materials Info */}
          <div className="text-xs text-muted-foreground p-3 bg-muted/20 rounded-lg">
            <p className="font-medium mb-1">Note:</p>
            <p>Default materials from the system database are shown in the dropdown. Add your own custom materials above to extend the list. Custom materials can be edited or removed anytime.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomMaterialsManager;
